// ─────────────────────────────────────────────────────────────
// MaintenanceBanner — sticky warning banner shown at the very top
// of the app when maintenance mode is on AND the current user is
// an admin. Regular users never see this — they see the full
// MaintenanceScreen instead.
//
// Behaviour:
//   • Sits above the existing top tab bar / nav rail.
//   • Dismissable per browser session (sessionStorage flag) so an
//     admin who's actively working through a maintenance window
//     can hide it temporarily but it returns on reload if the
//     window is still open.
//   • [Disable] button calls back to the parent — the parent
//     decides whether to navigate to the admin panel or invoke
//     the API directly.
// ─────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { Alert, Box, Button } from "@mui/material";
import { formatRelativeTime } from "../utils/formatRelativeTime";

const SESSION_KEY = "maintenance_banner_dismissed";

export interface MaintenanceBannerProps {
  message: string | null;
  updatedAt: string | null;
  onDisable: () => void;
}

export function MaintenanceBanner({
  message,
  updatedAt,
  onDisable,
}: MaintenanceBannerProps) {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      return false;
    }
  });

  // If the user dismisses the banner, we want it to re-appear on the
  // next page load (in case maintenance is still on). Per-session
  // storage is the right scope.
  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      // sessionStorage can throw in private mode — gracefully degrade.
    }
    setDismissed(true);
  };

  // When maintenance mode flips off elsewhere (the parent un-mounts the
  // banner), clear the flag so subsequent maintenance windows show
  // the banner again from a fresh state. This effect runs at mount;
  // the banner only mounts when maintenance is on, so an unmount
  // (= maintenance turned off) means we should reset on next mount.
  useEffect(() => {
    return () => {
      try {
        sessionStorage.removeItem(SESSION_KEY);
      } catch {
        // ignore
      }
    };
  }, []);

  if (dismissed) return null;

  const updatedRelative = updatedAt ? formatRelativeTime(updatedAt) : null;
  const messageFragment = message ? ` ${message}` : "";
  const timeFragment = updatedRelative ? ` — ${updatedRelative}` : "";

  return (
    <Alert
      severity="warning"
      variant="filled"
      onClose={handleDismiss}
      sx={{
        borderRadius: 0,
        position: "sticky",
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 2,
      }}
      action={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={onDisable}
            sx={{
              borderColor: "rgba(255,255,255,0.6)",
              color: "inherit",
              textTransform: "none",
              "&:hover": {
                borderColor: "rgba(255,255,255,0.9)",
                bgcolor: "rgba(255,255,255,0.08)",
              },
            }}
          >
            Disable
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
        <strong>Maintenance mode is ON.</strong>
        <span>Regular users are locked out.</span>
        {messageFragment && <em>{messageFragment}</em>}
        {timeFragment && <span style={{ opacity: 0.85 }}>{timeFragment}</span>}
      </Box>
    </Alert>
  );
}
